import { EditorContext, Extensions, useEditor } from '@tiptap/react';

import cn from 'classnames';

import DisableAllShortcuts from './plugins/DisableAllShortcuts';
import PlainTextHardBreak from './plugins/plain-text/PlainTextHardBreak';
import NoCopyPaste from './plugins/NoCopyPaste';
import PlaceholderExtended from './plugins/PlaceholderExtended';
import UniqueId from './plugins/UniqueId';
import { PlainTextDocument } from './plugins/PlainTextDocument';
import { EditorProps } from '@tiptap/pm/view';
import Content, { generateJson, generateParagraphContent } from './plugins/Content';
import StarterKit from '@tiptap/starter-kit';
import { MutableRefObject, useRef } from 'react';
import { EditorRendererContext } from './Editor';
import Comment from './plugins/comment';
import ReviewerCopilot from './plugins/ReviewerCopilot';
import { Fragment, Node } from 'prosemirror-model';
import Alert from './plugins/Alert';
import TextDirection from './plugins/TextDirection';

interface PlainTextEditorContextProps {
  children: React.ReactNode;
  extensions?: Extensions;
  initialContent?: string;
  editorProps?: EditorProps;
}

export default function PlainTextEditorContext({
  children,
  extensions,
  initialContent,
  editorProps,
}: PlainTextEditorContextProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const mentions = extensions?.find(extension => extension.name === 'mention')?.options;
  const content = generateJson(initialContent ?? '', mentions);
  const editor = useEditor(
    {
      content,
      immediatelyRender: false,
      parseOptions: { preserveWhitespace: 'full' },
      extensions: [
        UniqueId,
        // Markdown.configure({ breaks: true }),
        Content.configure({
          type: 'text',
        }),
        StarterKit.configure({
          blockquote: false,
          bold: false,
          bulletList: false,
          code: false,
          codeBlock: false,
          heading: false,
          listItem: false,
          orderedList: false,
          strike: false,
          italic: false,
          hardBreak: false,
        }),
        PlainTextHardBreak,
        PlaceholderExtended,
        NoCopyPaste,
        DisableAllShortcuts,
        PlainTextDocument,
        Comment.configure({
          classes: {
            base: 'comment hover:cursor-pointer',
            selection: 'bg-warning-100',
            comment: 'bg-primary-100 hover:bg-primary-200',
            selectedComment: 'bg-primary-200',
          },
        }),
        ReviewerCopilot,
        Alert,
        TextDirection,
        ...(extensions ?? []),
      ],
      // set initial editor options, do not add dynamic options here!
      editorProps: {
        attributes: {
          class: cn(
            'relative wysiwyg wysiwyg-sm max-w-none [&>pre]:p-2 [&>pre]:rounded-lg wysiwyg-p:text-neutral-800 focus:outline-none min-h-[60px]',
          ),
        },
        ...editorProps,
        handlePaste(view, event) {
          // Get the pasted text
          const pastedText = event.clipboardData?.getData('text/plain');
          if (!pastedText) {
            return false;
          }

          const paragraphContent = generateParagraphContent(pastedText, mentions);
          const nodes = paragraphContent
            .map(node => {
              if (node.type === 'text') {
                return view.state.schema.text(node.text);
              } else if (node.type === 'hardBreak') {
                return view.state.schema.node('hardBreak');
              } else if (node.type === 'mention') {
                return view.state.schema.node('mention');
              }
              return null;
            })
            .filter(Boolean);

          // Create a fragment from the nodes array
          const fragment = Fragment.fromArray(nodes as Node[]);

          // Insert the fragment into the editor
          const transaction = view.state.tr.replaceSelectionWith(
            fragment as unknown as Node,
            false,
          );
          view.dispatch(transaction);

          // Prevent the default behavior
          return true;
        },
      },
    },
    [],
  );

  if (!editor) {
    return null;
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      <EditorRendererContext.Provider
        value={{ editorRef: editorRef as MutableRefObject<HTMLDivElement> }}
      >
        {children}
      </EditorRendererContext.Provider>
    </EditorContext.Provider>
  );
}
